import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi, InviteUserParams, UpdateUserParams } from '../api/auth';
import { useToast } from '../components/ui/Toast';

export function useUsers(enabled = true) {
  return useQuery({
    queryKey: ['users'],
    queryFn: authApi.listUsers,
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (params: InviteUserParams) => authApi.createUser(params),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      showToast(data.message, 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to invite user', 'error');
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (userId: string) => authApi.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      showToast('User deleted', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete user', 'error');
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ userId, params }: { userId: string; params: UpdateUserParams }) =>
      authApi.updateUser(userId, params),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      showToast(data.message, 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update user', 'error');
    },
  });
}

export function useAdminResetPassword() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (userId: string) => authApi.adminResetPassword(userId),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      showToast(data.message, 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to reset password', 'error');
    },
  });
}
